/* eslint-disable max-len */
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import XLSX from 'sheetjs-style-v2';
import {Segment, SegmentModel, TheTradeDeskCategoryPricingDTO} from '../../segment.model';
import {BehaviorSubject} from 'rxjs';
import {SegmentsListFilter} from '../../segments.state';
import {ExportPreparerService} from '../export-preparer.service';
import {SubSink} from 'subsink';
import {ApiVersion} from '../../../app.types';
import {DDP_PRICE_UNIT_SIZE} from '../../info/shared-targets-list/account-google-ddp/domain';

const TRUNCATE_NO = 50;
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L',
  'N', 'M', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
  'CA', 'CB', 'CC', 'CD', 'CE', 'CF', 'CG', 'CH', 'CI', 'CJ', 'CK', 'CL',
  'CN', 'CM', 'CO', 'CP', 'CQ', 'CR', 'CS', 'CT', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ',
  'BA', 'BB', 'BC', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BK', 'BL',
  'BN', 'BM', 'BO', 'BP', 'BQ', 'BR', 'BS', 'BT', 'BU', 'BV', 'BW', 'BX', 'BY', 'BZ',
  'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH', 'AI', 'AJ', 'AK', 'AL',
  'AN', 'AM', 'AO', 'AP', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AV', 'AW', 'AX', 'AY', 'AZ',
];

@Component({
  selector: 'app-export',
  templateUrl: './export-preview-dialog.component.html',
  styleUrls: ['./export-preview-dialog.component.scss']
})
export class ExportPreviewDialogComponent implements OnInit, OnDestroy {

  dataSource: SegmentModel[] = [];
  displayedColumns: string[] = [];
  truncatedCount = 0;
  separatorPrefix = 'Category:';
  downloading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // actually it's a partial SegmentModel with additional attributes, that vary with shares
  private filteredSegmentsWithCategoryHeaders!: any[];
  private subSink = new SubSink();

  constructor(
    private dialogRef: MatDialogRef<ExportPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ExportPreviewDialogData,
    private exportPreparer: ExportPreparerService
  ) {
  }

  ngOnInit(): void {
    this.parseSegmentData(this.data.segments);
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  downloadXLSX() {
    this.downloading$.next(true);
    this.exportPreparer.fetchAllSegmentsToExport(this.data.previousFilter, this.data.apiVersion);
    this.subSink.sink = this.exportPreparer.listenToSegmentsForExport()
      .subscribe(segments => {
        this.parseSegmentData(segments);
        const titleFilename = this.data.title.replace(/\s/g, '_').toLowerCase().replace(/[^öäüa-z\d\-_]/g, '');
        const sheetName = 'segments';

        const headerRowName = Object.keys(this.filteredSegmentsWithCategoryHeaders[0]).slice(1); // columns name
        const wscols = headerRowName.map(headerName => {  // columns length added
          return {wch: headerName.length + 3};
        });
        // header rows are inferred from object keys
        const segmentsWS = XLSX.utils.json_to_sheet(this.filteredSegmentsWithCategoryHeaders);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, segmentsWS, sheetName);
        const ws = wb.Sheets[sheetName];
        ws['!cols'] = [{wch: 60}, ...wscols];
        Object.keys(ws)
          .forEach(cell => {
            try {
              if (ws[cell].v.includes(';')) {
                ws[cell].s = {alignment: {wrapText: true}};
              }
            } catch (e) {
              // do nothing
            }
          });

        this.fillRowWithColor(ws, 1, 'FF02748B', 'FFFFFFFF');

        this.filteredSegmentsWithCategoryHeaders.forEach((row, srcRowIdx) => {
          if (this.isSeparatorRow(row.name)) {
            this.fillRowWithColor(ws, srcRowIdx + 2, 'FFFFFF00', 'FFFF0000');
          }
        });

        XLSX.writeFile(wb, titleFilename + '.xlsx');
        this.dialogRef.close();
      });
  }

  fillRowWithColor(ws: XLSX.WorkSheet, rowIdx: number, fillColor: string, textColor: string) {
    LETTERS.forEach((letter) => {

      const cell = ws[letter + '' + rowIdx];
      if (cell) {
        cell.s = {
          font: {
            color: {rgb: textColor},
          },
          fill: {
            patternType: 'solid',
            bgColor: {rgb: fillColor},
            fgColor: {rgb: fillColor}
          }
        };
      }
    });
  }

  isSeparatorRow(name: string) {
    return RegExp('^' + this.separatorPrefix).exec(name);
  }

  private parseSegmentData(segments: Segment[]) {
    function getPartnerName(it: TheTradeDeskCategoryPricingDTO): string {
      return it.partnerId === 'System' ? 'Category Price' : `${it.partnerName}(${it.partnerId})`;
    }

    let filteredSegments: any[] = segments.map(segment => {
      const segmentRow: { [key: string]: any } = {
        id: segment.id,
        name: segment.name,
        description: segment.description,
        partnerId: segment.partnerId,
        category: segment.category,
        mainType: segment.mainType,
        subType: segment.subType,
        audienceType: segment.audienceType,
        clientSideId: segment.clientSideId,
        reachMerged: segment.reach?.merged,
        reachXpredict: segment.reach?.xpredict,
        reachNarthaPure: segment.reach?.narthaPure,
        reachNarthaConverted: segment.reach?.narthaConverted,
      };
      segment.platformAttributes?.forEach(
        sharingInfo => {
          const accountInfo = sharingInfo.distributionAccount;
          const accountName: string = (accountInfo?.description + ' foreign ID(s)' || 'account name' + accountInfo.targetSystemName);
          segmentRow[accountName] = sharingInfo?.foreignId;
          if (sharingInfo.platformAttributes.ttdPricing.length >= 1) {
            segmentRow[accountName + ' segment pricing'] = sharingInfo
              .platformAttributes.ttdPricing
              .map(it =>
                `${it.partnerName}(${it.partnerId}):${it.cpm?.toFixed(2) || 'NO_PRICE'}$ ${it.percentOfMediaCostRate || 'PERCENT_NOT_SET'}%${it.type}`)
              .join(';');
          }
          if (sharingInfo.platformAttributes.ttdCompliance) {
            segmentRow[accountName + ' compliance'] = sharingInfo.platformAttributes.ttdCompliance;
          }
          if (sharingInfo.platformAttributes.activeAgentAttributes.length >= 1) {
            segmentRow[accountName] = sharingInfo.platformAttributes.activeAgentAttributes
              .map(s => s.activeAgentSegmentId).join(';');
            segmentRow[accountName + ' attribute pricing'] = sharingInfo.platformAttributes.activeAgentAttributes
              .map(s => `${s.activeAgentSegmentId}(${s.attributeName}):${s.cpm ? (s.cpm / 100).toFixed(2) : 'NO_PRICE'}$`).join(';');
          }
          if (sharingInfo.platformAttributes.googleAttributes.length >= 1) {
            segmentRow[accountName + ' attributes'] = sharingInfo.platformAttributes.googleAttributes
              .map(s => `(${s.clientId || 'GLOBAL'}):${s.cost !== 0 ? (s.cost / DDP_PRICE_UNIT_SIZE).toFixed(2) : 'NO_PRICE'}${s.currencyCode}`).join(';');
          }
        }
      );
      return segmentRow;
    });

    const allKeys = new Set<string>(filteredSegments.flatMap(s => Object.keys(s)));
    filteredSegments = filteredSegments.sort((a, b) => a.category?.localeCompare(b.category));
    const filteredSegmentsWithCategoryHeaders: SegmentModel[] = [];

    let lastCategory: string;
    // TODO: What is this? What does this and why is it so convoluted
    filteredSegments.forEach(segment => {
      if (lastCategory !== segment.category) {
        const separatorRow: {
          [key: string]: any
        } = {name: this.separatorPrefix + ' ' + (segment.category?.trim() || 'none')};
        allKeys.forEach((k: string) => separatorRow[k] = separatorRow[k] as string);
        filteredSegmentsWithCategoryHeaders.push(separatorRow as SegmentModel);
        lastCategory = segment.category;
      }
      allKeys.forEach(k => segment[k] = segment[k] || '');
      filteredSegmentsWithCategoryHeaders.push(segment);

    });

    this.displayedColumns = Array.from(allKeys);
    this.dataSource = filteredSegmentsWithCategoryHeaders.slice(0, TRUNCATE_NO);
    this.truncatedCount = this.data.segmentsTotalPageCount - TRUNCATE_NO + 1;
    this.filteredSegmentsWithCategoryHeaders = filteredSegmentsWithCategoryHeaders;
  }
}

export type ExportPreviewDialogData = {
  title: string,
  segments: Segment[],
  segmentsTotalPageCount: number,
  previousFilter: SegmentsListFilter,
  apiVersion: ApiVersion
};
